import { observable, configure, action, computed } from 'mobx';

import util from 'preact-util';

import {
    readGuestCart,
    writeGuestCart,
    addGuestLine,
    setGuestQty,
    removeGuestLine,
    guestCartCount,
    guestCartTotal,
    toCheckoutLines,
    validateGuestForm,
    readGuestPending,
    writeGuestPending,
} from '../lib/guestCart';

configure({ enforceActions: 'always' });

// Basket + checkout state for visitors who are NOT logged in. One basket per
// customer (shop), persisted in localStorage via lib/guestCart.js. The paid
// flow is: checkout() → POST /api/sales/guest-checkout → browser redirect to
// Vipps (ePayment) → /api/vipps/return settles the sale → back on the race
// page load() → resumePending() polls getStatus() → markPaid().
const PENDING_CHECKS = 3;
const PENDING_CHECK_DELAY_MS = 1000;
const emptyForm = () => ({
    firstname: '', lastname: '', cellphone: '', email: '',
});

function defaultNavigate(url) {
    if (typeof window !== 'undefined') {
        window.location.assign(url);
    }
}

const defaultWait = ms => new Promise((resolve) => { setTimeout(resolve, ms); });

function defaultStorage() {
    try {
        return typeof window !== 'undefined' ? window.localStorage : null;
    } catch (err) {
        return null;
    }
}

class GuestCartStore {
    constructor({ storage, navigate = defaultNavigate, wait = defaultWait } = {}) {
        this.storage = storage === undefined ? defaultStorage() : storage;
        this.navigate = navigate;
        this.wait = wait;
    }

    @action
    updateKeyValue(key, value) {
        this[key] = value;
    }

    @action
    updateObjectKeyValue(obj, key, value) {
        this[obj][key] = value;
    }

    @observable customer = 0;

    @observable lines = [];

    @observable form = emptyForm();

    @observable formErrors = {};

    @observable canCheckout = false;

    @observable checkoutError = '';

    @observable checkingOut = false;

    @observable redirecting = false;

    @observable returnedFromVipps = false;

    @observable paymentCancelled = false;

    @observable sale = null;

    @observable paid = false;

    @computed get count() {
        return guestCartCount(this.lines);
    }

    @computed get total() {
        return guestCartTotal(this.lines);
    }

    persist() {
        writeGuestCart(this.storage, this.customer, this.lines);
    }

    @action
    setFormField(name, value) {
        this.form = { ...this.form, [name]: value };
        if (this.formErrors[name]) {
            const { [name]: removed, ...rest } = this.formErrors;
            this.formErrors = rest;
        }
    }

    @action
    async load(customer) {
        const customerId = parseInt(customer, 10) || 0;
        this.customer = customerId;
        this.lines = readGuestCart(this.storage, customerId);
        if (!customerId) {
            this.canCheckout = false;
            return;
        }
        const response = await util.fetchApi(`/api/customers/public/${customerId}`, { method: 'GET' }, {});
        const methods = (response && response.status === 200 && response.data && response.data.onlinePaymentMethods) || [];
        this.updateKeyValue('canCheckout', methods.includes('vipps'));
        this.updateKeyValue('returnedFromVipps', await this.resumePending());
    }

    // Back from Vipps: get-return.js has already settled the payment before it
    // redirected here, so the first check normally answers. A payment still
    // open after a few checks was left on the Vipps page (Back button).
    async resumePending() {
        const pending = readGuestPending(this.storage, this.customer);
        if (!pending) {
            return false;
        }
        let paymentStatus = 0;
        for (let check = 0; check < PENDING_CHECKS; check += 1) {
            if (check > 0) {
                await this.wait(PENDING_CHECK_DELAY_MS);
            }
            const response = await this.getStatus(pending.paymentUuidv4);
            paymentStatus = (response && response.data && response.data.paymentStatus) || 0;
            if (paymentStatus >= 2) {
                break;
            }
        }
        writeGuestPending(this.storage, this.customer, null);
        if (paymentStatus >= 2 && paymentStatus < 90) {
            this.updateKeyValue('sale', pending.sale || null);
            this.setFormField('email', pending.email || '');
            this.markPaid();
        } else {
            this.updateKeyValue('paymentCancelled', true);
        }
        return true;
    }

    @action
    add({
        product, qty = 1, color = '', size = '', race = 0,
    }) {
        if (!product || !product.id || product.customer !== this.customer) {
            return false;
        }
        // A finished order stays on screen only until the next add: the drawer
        // may have been swiped away without its Close button running clear().
        if (this.paid) {
            this.paid = false;
            this.sale = null;
            this.checkoutError = '';
        }
        this.lines = addGuestLine(this.lines, {
            product, qty, color, size, race,
        });
        this.persist();
        return true;
    }

    @action
    setQty(key, qty) {
        this.lines = setGuestQty(this.lines, key, qty);
        this.persist();
    }

    @action
    remove(key) {
        this.lines = removeGuestLine(this.lines, key);
        this.persist();
    }

    @action
    clear() {
        this.lines = [];
        this.persist();
        this.sale = null;
        this.paid = false;
        this.paymentCancelled = false;
        this.checkoutError = '';
        this.formErrors = {};
    }

    @action
    markPaid() {
        this.paid = true;
        this.lines = [];
        this.persist();
    }

    async checkout({ race = 0, wantedPage = '' } = {}) {
        const formErrors = validateGuestForm(this.form);
        this.updateKeyValue('formErrors', formErrors);
        this.updateKeyValue('checkoutError', '');
        this.updateKeyValue('paymentCancelled', false);
        if (Object.keys(formErrors).length > 0 || this.lines.length === 0) {
            return false;
        }
        this.updateKeyValue('checkingOut', true);
        const response = await util.fetchApi('/api/sales/guest-checkout', { method: 'POST' }, {
            customer: this.customer,
            race,
            lines: toCheckoutLines(this.lines),
            visitor: { ...this.form },
            wantedPage,
        });
        this.updateKeyValue('checkingOut', false);
        if (response && response.status === 200 && response.data && response.data.redirectUrl) {
            const { redirectUrl, paymentUuidv4, sale } = response.data;
            writeGuestPending(this.storage, this.customer, { paymentUuidv4, sale: sale || null, email: this.form.email });
            this.updateKeyValue('sale', sale || null);
            this.updateKeyValue('redirecting', true);
            this.navigate(redirectUrl);
            return true;
        }
        const errors = response && response.data && response.data.errors;
        if (Array.isArray(errors) && errors.every(e => typeof e === 'string')) {
            const fieldErrors = {};
            errors.forEach(field => { fieldErrors[field] = true; });
            this.updateKeyValue('formErrors', fieldErrors);
        }
        this.updateKeyValue('checkoutError', (response && response.message) || 'Checkout failed.');
        return false;
    }

    async getStatus(uuidv4) {
        return util.fetchApi(`/api/vipps/checkout-session-status-public/${uuidv4}`, { method: 'GET' }, {});
    }
}

const store = new GuestCartStore();

export { GuestCartStore };
export default store;
